import { useMutation } from '@apollo/client';
import * as _ from 'lodash-es';
import type React from 'react';
import { useCallback, useMemo, useReducer } from 'react';
import type { SetRequired } from 'type-fest';
import {
  LOCAL_TOPIC_ID_PREFIX,
  MoveStates,
  PostStatus,
  TopicStatus,
  UNORGANIZED_TOPIC_ID,
} from './constants';
import type { AllTopicsTreeListTopic } from './useAllTopics';
import { gql } from '../graphql/generated/gql';
import type { ReorganizeOperation } from '../graphql/generated/graphql';
import {
  ReorganizeOperationName,
  TopicMemberEditable,
  TopicPrivacy,
} from '../graphql/generated/graphql';
import Logger from '../utils/Logger';
import getPostsOperations from './utils/getPostsOperations';
import isReorganizationPostEdgePinned from './utils/isReorganizationPostEdgePinned';

const ROOT_TOPIC_ID = 'root-topic-id';

export type TopicForReorganization = Pick<
  AllTopicsTreeListTopic,
  | 'id'
  | 'name'
  | 'privacy'
  | 'memberEditable'
  | 'order'
  | 'inheritedAncestors'
  | 'owners'
  | 'ownerGroups'
  | 'members'
  | 'memberGroups'
> & {
  parent?: { id: string } | null;
  ancestors: { id: string }[];
  childTopics: TopicForReorganization[];
};

export interface ReorganizationPostEdge extends PostEdge {
  isParentSelected: boolean;
  isSelected: boolean;
  reorganizationMetadata: PostReorganizationMetadata;
  postKey: string;
  topicId: string;
}

type TopicPostsWrapper<TEdge extends PostEdge> =
  | { status: 'loaded'; edges: TEdge[] }
  | { status: 'cached'; postIds: string[] };

export type TopicPosts = TopicPostsWrapper<PostEdge>;
type ReorganizationTopicPosts = TopicPostsWrapper<ReorganizationPostEdge>;

export interface TopicReorganizationTree {
  topic: TopicForReorganization;
  isParentSelected: boolean;
  isSelected: boolean;
  reorganizationTopicPosts: ReorganizationTopicPosts;
  reorganizationMetadata: TopicReorganizationMetadata;
  selected: {
    topicIds: string[];
    postKeys: string[];
    pinnedPostKeys: string[];
  };
  children: TopicReorganizationTree[];
  parentTree?: TopicReorganizationTree;
}

type MoveStatesMetadata =
  | { state: MoveStates.initial }
  | { state: MoveStates.movedIn; source: TopicForReorganization | null }
  | { state: MoveStates.movedOut };

export interface TopicReorganizationMetadata {
  move: MoveStatesMetadata;
  status: TopicStatus;
  isNew?: boolean;
  name?: string | null;
}

export interface PostReorganizationMetadata {
  move: MoveStatesMetadata;
  status: PostStatus;
  isPinned?: boolean | null;
}

const REORGANIZE_MUTATION = gql(/** @complexity 2 */ `
  mutation reorganize($operations: [ReorganizeOperation!]!) {
    reorganize(operations: $operations)
  }
`);

const DefaultSubtopicName = 'Topic name';

const getSubtopicPlaceholder = (id: string) =>
  createSpecialTopic({
    id,
    name: DefaultSubtopicName,
    privacy: TopicPrivacy.Open,
    memberEditable: TopicMemberEditable.All,
    parent: null,
    childTopics: [],
  });

const RESET = 'RESET';
const POST_MOVE = 'POST_MOVE';
const PINNED_POST_MOVE = 'PINNED_POST_MOVE';
const TOPIC_MOVE = 'TOPIC_MOVE';
const TOPIC_SELECTED_SET = 'TOPIC_SELECTED_SET';
const TOPIC_DELETED_SET = 'TOPIC_DELETED_SET';
const TOPIC_NAME_SET = 'TOPIC_NAME_SET';
const SUBTOPIC_CREATE = 'SUBTOPIC_CREATE';
const POST_SELECTED_SET = 'POST_SELECTED_SET';
const POST_PINNED_SET = 'POST_PINNED_SET';
const POST_ARCHIVED_SET = 'POST_ARCHIVED_SET';
const POST_DELETED_SET = 'POST_DELETED_SET';

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const getPostKey = (postId: string, topicId: string) =>
  [topicId, postId].join(':');
const decodePostKey = (postKey: string): [string, string] =>
  postKey.split(':') as [string, string];
const getPostOriginalParentId = (postKey: string) => decodePostKey(postKey)[0];

interface State {
  destinationIdByPostKey: Map<string, string>;
  orderedPinnedPostKeysByTopicId: Map<string, string[]>;
  destinationIdByTopicId: Map<string, string>;
  orderedChildrenIdsByTopicId: Map<string, string[]>;
  selectedTopicIds: Set<string>;
  statusByTopicIds: Map<string, TopicStatus>;
  nameByTopicIds: Map<string, string>;
  createdTopicIds: Set<string>;
  selectedPostKeys: Set<string>;
  statusByPostKeys: Map<string, PostStatus>;
  pinnedByPostKeys: Map<string, boolean>;
}

const generateInitialState = (): State => ({
  destinationIdByPostKey: new Map(),
  orderedPinnedPostKeysByTopicId: new Map(),
  destinationIdByTopicId: new Map(),
  orderedChildrenIdsByTopicId: new Map(),
  selectedTopicIds: new Set(),
  statusByTopicIds: new Map(),
  nameByTopicIds: new Map(),
  createdTopicIds: new Set(),
  selectedPostKeys: new Set(),
  statusByPostKeys: new Map(),
  pinnedByPostKeys: new Map(),
});

const getDeletedTopicIds = (
  statusByTopicIds: Map<string, TopicStatus>,
): string[] => {
  const ids: string[] = [];
  statusByTopicIds.forEach((status, id) => {
    if (status === TopicStatus.deleted) ids.push(id);
  });
  return ids;
};

export type PostEdge = {
  postId: string;
  pinnedOrder: number | null;
};

type MovedInPost = [edge: PostEdge, sourceId: string];

interface Data {
  rootTopic: TopicForReorganization;
  topicPostsMap: Record<string, TopicPosts> | null;
  topicMap: Map<string | null, TopicForReorganization>;
}

const getMovedInPostMap = (
  { destinationIdByPostKey }: State,
  { topicPostsMap }: Data,
) => {
  const movedInPosts: Record<string, MovedInPost[]> = {};

  Object.entries(topicPostsMap || {}).forEach(([sourceId, topicPosts]) => {
    if (topicPosts.status !== 'loaded') return;
    topicPosts.edges.forEach((edge) => {
      const postKey = getPostKey(edge.postId, sourceId);
      const newTopicId = destinationIdByPostKey.get(postKey);
      if (newTopicId) {
        const movedInPostsForTopicId = movedInPosts[newTopicId] || [];
        movedInPostsForTopicId.push([edge, sourceId]);
        movedInPosts[newTopicId] = movedInPostsForTopicId;
      }
    });
  });

  return movedInPosts;
};

export const isReorganizationPostEdgeArchived = ({
  reorganizationMetadata: { move, status },
}: ReorganizationPostEdge): boolean => {
  return move.state !== MoveStates.movedOut && status === PostStatus.archived;
};

export const isReorganizationPostEdgeDeleted = ({
  reorganizationMetadata: { move, status },
}: ReorganizationPostEdge): boolean => {
  return move.state !== MoveStates.movedOut && status === PostStatus.deleted;
};

export enum ReorganizationPostEdgeAction {
  delete = 'delete',
  restore = 'restore',
  pin = 'pin',
  unpin = 'unpin',
  archive = 'archive',
  unarchive = 'unarchive',
}

export const reorganizationPostEdgeActionCheck = (
  edge: ReorganizationPostEdge,
  topicId: string,
  action: ReorganizationPostEdgeAction,
): boolean => {
  const isDeleted = isReorganizationPostEdgeDeleted(edge);
  switch (action) {
    case ReorganizationPostEdgeAction.delete:
      return !isDeleted;
    case ReorganizationPostEdgeAction.restore:
      return isDeleted;
    case ReorganizationPostEdgeAction.pin:
      return (
        !isDeleted &&
        topicId !== UNORGANIZED_TOPIC_ID &&
        !isReorganizationPostEdgePinned(edge)
      );
    case ReorganizationPostEdgeAction.unpin:
      return (
        !isDeleted &&
        topicId !== UNORGANIZED_TOPIC_ID &&
        isReorganizationPostEdgePinned(edge)
      );
    case ReorganizationPostEdgeAction.archive:
      return !isDeleted && !isReorganizationPostEdgeArchived(edge);
    case ReorganizationPostEdgeAction.unarchive:
      return !isDeleted && isReorganizationPostEdgeArchived(edge);
    default:
      return false;
  }
};

export interface SelectedPosts {
  all: string[];
  byAllowedActions: Record<ReorganizationPostEdgeAction, string[]>;
}

interface ApplyChangesResult {
  tree: TopicReorganizationTree;
  treeById: Map<string, TopicReorganizationTree>;
  selectedPosts: SelectedPosts;
  changeCount: number;
}

const applyChanges = (data: Data, state: State): ApplyChangesResult => {
  const {
    destinationIdByPostKey,
    orderedPinnedPostKeysByTopicId,
    destinationIdByTopicId,
    orderedChildrenIdsByTopicId,
    selectedTopicIds,
    statusByTopicIds,
    nameByTopicIds,
    selectedPostKeys,
    statusByPostKeys,
    pinnedByPostKeys,
    createdTopicIds,
  } = state;
  const { rootTopic, topicPostsMap, topicMap } = data;
  const selectedPosts: SelectedPosts = {
    all: [],
    byAllowedActions: Object.values(ReorganizationPostEdgeAction).reduce(
      (actionToPostKeys, action) => ({
        ...actionToPostKeys,
        [action]: [],
      }),
      {} as Record<ReorganizationPostEdgeAction, string[]>,
    ),
  };

  const movedInPostMap = getMovedInPostMap(state, data);
  const treeById = new Map<string, TopicReorganizationTree>();

  const calculateTree = (
    topicId: string,
    {
      ancestors,
      isParentSelected,
    }: {
      ancestors: TopicForReorganization['ancestors'];
      isParentSelected: boolean;
    },
  ): TopicReorganizationTree => {
    const topic = topicMap.get(topicId) || getSubtopicPlaceholder(topicId);
    const parent = ancestors[0] ?? { id: ROOT_TOPIC_ID };

    const reorganizationMetadata: TopicReorganizationMetadata = {
      move: { state: MoveStates.initial },
      status: statusByTopicIds.get(topicId) || TopicStatus.initial,
      isNew: createdTopicIds.has(topicId),
      name: nameByTopicIds.get(topicId) || null,
    };
    if (destinationIdByTopicId.has(topicId) && !createdTopicIds.has(topicId)) {
      const destinationId = destinationIdByTopicId.get(topicId);

      const originalParentId = topic.parent?.id ?? ROOT_TOPIC_ID;
      reorganizationMetadata.move =
        destinationId === parent.id
          ? {
              state: MoveStates.movedIn,
              source: topicMap.get(originalParentId) ?? null,
            }
          : { state: MoveStates.movedOut };
    }
    if (destinationIdByTopicId.has(topicId) && createdTopicIds.has(topicId)) {
      const destinationId = destinationIdByTopicId.get(topicId);

      if (destinationId !== parent.id) {
        reorganizationMetadata.move = { state: MoveStates.movedOut };
      }
    }

    // Selected state
    const isExplicitlySelected = topicId
      ? selectedTopicIds.has(topicId)
      : false;
    const isSelected = isParentSelected || isExplicitlySelected;
    const selected: TopicReorganizationTree['selected'] = {
      topicIds: isExplicitlySelected && topicId ? [topicId] : [],
      postKeys: [],
      pinnedPostKeys: [],
    };

    let subtopicIds: string[] = [];
    let reorganizationTopicPosts: ReorganizationTopicPosts = {
      status: 'cached',
      postIds: [],
    };
    if (reorganizationMetadata.move.state !== MoveStates.movedOut) {
      subtopicIds =
        orderedChildrenIdsByTopicId.get(topicId) ??
        topic.childTopics.map(({ id }) => id);

      const getPostStatusAndPinnedMetadata = (postKey: string) => {
        const status = statusByPostKeys.get(postKey) || PostStatus.initial;
        return {
          status,
          isPinned: pinnedByPostKeys.has(postKey)
            ? pinnedByPostKeys.get(postKey)
            : null,
        };
      };

      const topicPostWithMoveState = (
        postEdge: PostEdge,
        postKey: string,
        move: PostReorganizationMetadata['move'],
        nonNullTopicId: string,
      ): ReorganizationPostEdge => {
        const isPostExplicitlySelected = selectedPostKeys.has(postKey);

        const isPostSelected = isSelected || isPostExplicitlySelected;
        const postReorganizationMetadata = {
          move,
          ...getPostStatusAndPinnedMetadata(postKey),
        };
        const reorganizationPostEdge: ReorganizationPostEdge = {
          ...postEdge,
          postKey,
          topicId: nonNullTopicId,
          isParentSelected: isSelected,
          isSelected: isPostSelected,
          reorganizationMetadata: postReorganizationMetadata,
        };

        if (isPostExplicitlySelected) {
          if (isReorganizationPostEdgePinned(reorganizationPostEdge)) {
            selected.pinnedPostKeys.push(postKey);
          } else {
            selected.postKeys.push(postKey);
          }
        }

        if (isPostSelected && move.state !== MoveStates.movedOut) {
          selectedPosts.all.push(postKey);
          Object.values(ReorganizationPostEdgeAction).forEach((action) => {
            if (
              reorganizationPostEdgeActionCheck(
                reorganizationPostEdge,
                nonNullTopicId,
                action,
              )
            ) {
              selectedPosts.byAllowedActions[action].push(postKey);
            }
          });
        }

        return reorganizationPostEdge;
      };

      const movedInPosts = topicId
        ? (movedInPostMap[topicId] || []).map(([edge, sourceId]) =>
            topicPostWithMoveState(
              edge,
              getPostKey(edge.postId, sourceId),
              {
                state: MoveStates.movedIn,
                source: topicMap.get(sourceId) ?? null,
              },
              topicId,
            ),
          )
        : [];

      const movedInPostKeySet = new Set(movedInPosts.map((p) => p.postKey));
      const topicPosts = topicPostsMap?.[topicId];
      if (topicPosts?.status === 'loaded') {
        const allPosts = topicPosts.edges
          .map((edge) => {
            const postKey = getPostKey(edge.postId, topicId);

            return topicPostWithMoveState(
              edge,
              postKey,
              {
                state: destinationIdByPostKey.get(postKey)
                  ? MoveStates.movedOut
                  : MoveStates.initial,
              },
              topicId,
            );
          })
          .filter((p) => !movedInPostKeySet.has(p.postKey))
          .concat(movedInPosts);

        const orderedPinnedPostKeys =
          orderedPinnedPostKeysByTopicId.get(topicId);

        if (orderedPinnedPostKeys) {
          const unpinnedPosts = [...allPosts];
          const pinnedPosts = _.compact(
            orderedPinnedPostKeys.map(
              (postKey) =>
                _.remove(unpinnedPosts, (p) => p.postKey === postKey)[0],
            ),
          );

          reorganizationTopicPosts = {
            status: 'loaded',
            edges: [...pinnedPosts, ...unpinnedPosts],
          };
        } else {
          reorganizationTopicPosts = { status: 'loaded', edges: allPosts };
        }
      } else if (topicPosts?.status === 'cached') {
        reorganizationTopicPosts = {
          status: 'cached',
          postIds: topicPosts.postIds,
        };
      } else if (topicId.startsWith(LOCAL_TOPIC_ID_PREFIX)) {
        const orderedPinnedPostKeys =
          orderedPinnedPostKeysByTopicId.get(topicId);
        if (orderedPinnedPostKeys) {
          const unpinnedPosts = [...movedInPosts];
          const pinnedPosts = _.compact(
            orderedPinnedPostKeys.map(
              (postKey) =>
                _.remove(unpinnedPosts, (p) => p.postKey === postKey)[0],
            ),
          );
          reorganizationTopicPosts = {
            status: 'loaded',
            edges: [...pinnedPosts, ...unpinnedPosts],
          };
        } else {
          reorganizationTopicPosts = { status: 'loaded', edges: movedInPosts };
        }
      }
    }

    const children = subtopicIds.map((subtopicId) => {
      const subtree = calculateTree(subtopicId, {
        ancestors: [{ id: topicId }, ...ancestors],
        isParentSelected: isSelected,
      });

      Object.entries(selected).forEach(([key, ids]) => {
        selected[key as keyof TopicReorganizationTree['selected']] = [
          ...ids,
          ...subtree.selected[key as keyof TopicReorganizationTree['selected']],
        ];
      });

      return subtree;
    });

    const tree: TopicReorganizationTree = {
      topic: {
        id: topic.id,
        name: topic.name,
        privacy: topic.privacy,
        memberEditable: topic.memberEditable,
        inheritedAncestors: topic.inheritedAncestors,
        order: topic.order,
        owners: topic.owners,
        ownerGroups: topic.ownerGroups,
        members: topic.members,
        memberGroups: topic.memberGroups,
        parent,
        ancestors,
        childTopics: subtopicIds.map(
          (subtopicId) => treeById.get(subtopicId)!.topic,
        ),
      },
      children,
      isParentSelected,
      isSelected,
      reorganizationTopicPosts,
      reorganizationMetadata,
      selected,
    };

    tree.children.forEach((child) => {
      child.parentTree = tree; // eslint-disable-line no-param-reassign
    });
    treeById.set(topicId, tree);

    return tree;
  };

  const sizeWithoutNewTopics = (map: Map<string, unknown>): number => {
    const { size } = map;
    const interactions = [...map.keys()].filter((id) =>
      createdTopicIds.has(id),
    );
    return size - interactions.length;
  };

  return {
    tree: calculateTree(rootTopic.id, {
      ancestors: rootTopic.ancestors,
      isParentSelected: false,
    }),
    treeById,
    selectedPosts,
    changeCount:
      destinationIdByPostKey.size +
      statusByPostKeys.size +
      pinnedByPostKeys.size +
      sizeWithoutNewTopics(destinationIdByTopicId) +
      sizeWithoutNewTopics(nameByTopicIds) +
      _.xor(getDeletedTopicIds(statusByTopicIds), [...createdTopicIds]).length,
  };
};

interface PostMoveAction {
  type: typeof POST_MOVE;
  postKey: string;
  destinationId: string;
}

interface PinnedPostMoveAction {
  type: typeof PINNED_POST_MOVE;
  postKey: string;
  destinationId: string;
  indexWithPlaceholder: number;
}

interface TopicMoveAction {
  type: typeof TOPIC_MOVE;
  id: string;
  destinationId: string | null;
  indexWithPlaceholder: number;
}

interface TopicSelectedSetAction {
  type: typeof TOPIC_SELECTED_SET;
  id: string;
  selected: boolean;
}

interface TopicDeletedSetAction {
  type: typeof TOPIC_DELETED_SET;
  id: string;
  deleted: boolean;
}

interface TopicNameSetAction {
  type: typeof TOPIC_NAME_SET;
  id: string;
  name: string | null;
}

interface SubtopicCreateAction {
  type: typeof SUBTOPIC_CREATE;
  topicTempId: string;
  parentId: string | null;
}

interface PostSelectedSetAction {
  type: typeof POST_SELECTED_SET;
  postKey: string;
  selected: boolean;
}

interface PostPinnedSetAction {
  type: typeof POST_PINNED_SET;
  postKeys: string[];
  pinned: boolean;
}

interface PostArchivedSetAction {
  type: typeof POST_ARCHIVED_SET;
  postKeys: string[];
  archived: boolean;
}

interface PostDeletedSetAction {
  type: typeof POST_DELETED_SET;
  postKeys: string[];
  deleted: boolean;
}

interface ResetAction {
  type: typeof RESET;
}

type Action =
  | PostMoveAction
  | PinnedPostMoveAction
  | TopicMoveAction
  | TopicSelectedSetAction
  | TopicDeletedSetAction
  | TopicNameSetAction
  | SubtopicCreateAction
  | PostSelectedSetAction
  | PostPinnedSetAction
  | PostArchivedSetAction
  | PostDeletedSetAction
  | ResetAction;

type ActionArgs<Type> = Omit<Extract<Action, { type: Type }>, 'type'>;

const generateActions = (dispatch: React.Dispatch<Action>) => ({
  movePost: ({ postKey, destinationId }: ActionArgs<typeof POST_MOVE>) =>
    dispatch({
      type: POST_MOVE,
      postKey,
      destinationId,
    }),
  movePinnedPost: ({
    postKey,
    destinationId,
    indexWithPlaceholder,
  }: ActionArgs<typeof PINNED_POST_MOVE>) =>
    dispatch({
      type: PINNED_POST_MOVE,
      postKey,
      destinationId,
      indexWithPlaceholder,
    }),
  moveTopic: ({
    id,
    destinationId,
    indexWithPlaceholder,
  }: ActionArgs<typeof TOPIC_MOVE>) =>
    dispatch({
      type: TOPIC_MOVE,
      id,
      destinationId,
      indexWithPlaceholder,
    }),
  setTopicSelected: ({ id, selected }: ActionArgs<typeof TOPIC_SELECTED_SET>) =>
    dispatch({
      type: TOPIC_SELECTED_SET,
      id,
      selected,
    }),
  setTopicDeleted: ({ id, deleted }: ActionArgs<typeof TOPIC_DELETED_SET>) =>
    dispatch({
      type: TOPIC_DELETED_SET,
      id,
      deleted,
    }),
  setTopicName: ({ id, name }: ActionArgs<typeof TOPIC_NAME_SET>) =>
    dispatch({
      type: TOPIC_NAME_SET,
      id,
      name,
    }),
  createSubtopic: ({
    topicTempId,
    parentId,
  }: ActionArgs<typeof SUBTOPIC_CREATE>) =>
    dispatch({
      type: SUBTOPIC_CREATE,
      topicTempId,
      parentId,
    }),
  setPostSelected: ({
    postKey,
    selected,
  }: ActionArgs<typeof POST_SELECTED_SET>) =>
    dispatch({
      type: POST_SELECTED_SET,
      postKey,
      selected,
    }),
  setPostPinned: ({ postKeys, pinned }: ActionArgs<typeof POST_PINNED_SET>) =>
    dispatch({
      type: POST_PINNED_SET,
      postKeys,
      pinned,
    }),
  setPostArchived: ({
    postKeys,
    archived,
  }: ActionArgs<typeof POST_ARCHIVED_SET>) =>
    dispatch({
      type: POST_ARCHIVED_SET,
      postKeys,
      archived,
    }),
  setPostDeleted: ({
    postKeys,
    deleted,
  }: ActionArgs<typeof POST_DELETED_SET>) =>
    dispatch({
      type: POST_DELETED_SET,
      postKeys,
      deleted,
    }),
  reset: () => dispatch({ type: RESET }),
});

const moveIntoIndex = <T>(collection: T[], item: T, index: number): T[] => {
  // Support negative index, -1 means the end of the collection.
  const resolvedIndex = index >= 0 ? index : collection.length + 1 + index;

  const originalIndex = collection.findIndex((i) => i === item);
  const newCollection = _.without(collection, item);
  const isMovingBackwards = originalIndex >= 0 && originalIndex < resolvedIndex;
  newCollection.splice(resolvedIndex - (isMovingBackwards ? 1 : 0), 0, item);

  return newCollection;
};

interface MoveBackUnsetArgs<T> {
  itemKey: T;
  index: number;
  currentParentId: string;
  getOriginalParentId: (itemKey: T) => string;
  destinationIdByItemKey: Map<T, string>;
  getOriginalList: (parentId: string) => T[];
}

const movedBackUnset = <T>({
  itemKey,
  index,
  currentParentId,
  getOriginalParentId,
  destinationIdByItemKey,
  getOriginalList,
}: MoveBackUnsetArgs<T>) => {
  const originalParentId = getOriginalParentId(itemKey);
  if (originalParentId !== currentParentId) {
    return;
  }

  if (
    !destinationIdByItemKey.has(itemKey) ||
    currentParentId !== destinationIdByItemKey.get(itemKey)
  ) {
    return;
  }

  const originalList = getOriginalList(originalParentId);

  if (index < originalList.length && originalList[index] === itemKey) {
    destinationIdByItemKey.delete(itemKey);
  }
};

interface RemoveItemFromListArgs<T> {
  listMap: Map<string, T[]>;
  itemKey: T;
  sourceId: string;
  destinationIdByItemKey: Map<T, string>;
  getOriginalList: (parentId: string) => T[];
  getOriginalParentId: (itemKey: T) => string;
}

const removeItemFromList = <T>({
  listMap,
  itemKey,
  sourceId,
  destinationIdByItemKey,
  getOriginalList,
  getOriginalParentId,
}: RemoveItemFromListArgs<T>) => {
  const getList = (id: string) => listMap.get(id) || getOriginalList(id);

  const newSourceList = _.without(getList(sourceId), itemKey);
  if (_.isEqual(getOriginalList(sourceId), newSourceList)) {
    listMap.delete(sourceId);
  } else {
    listMap.set(sourceId, newSourceList);
  }

  newSourceList.forEach((key, index) => {
    movedBackUnset({
      index,
      getOriginalParentId,
      destinationIdByItemKey,
      getOriginalList,
      itemKey: key,
      currentParentId: sourceId,
    });
  });
};

interface AddItemToListArgs<T> {
  listMap: Map<string, T[]>;
  itemKey: T;
  indexWithPlaceholder: number;
  destinationId: string;
  destinationIdByItemKey: Map<T, string>;
  getOriginalList: (parentId: string) => T[];
  getOriginalParentId: (itemKey: T) => string;
}

const addItemToList = <T>({
  listMap,
  itemKey,
  indexWithPlaceholder,
  destinationId,
  destinationIdByItemKey,
  getOriginalList,
  getOriginalParentId,
}: AddItemToListArgs<T>) => {
  const getList = (id: string) => listMap.get(id) || getOriginalList(id);

  // Set new parents
  destinationIdByItemKey.set(itemKey, destinationId);

  // Calculate new destination
  const newDestinationList = moveIntoIndex(
    getList(destinationId),
    itemKey,
    indexWithPlaceholder,
  );

  if (_.isEqual(newDestinationList, getOriginalList(destinationId))) {
    listMap.delete(destinationId);
  } else {
    listMap.set(destinationId, newDestinationList);
  }

  newDestinationList.forEach((key, index) => {
    movedBackUnset({
      index,
      getOriginalParentId,
      destinationIdByItemKey,
      getOriginalList,
      itemKey: key,
      currentParentId: destinationId,
    });
  });
};

interface MoveListItemArgs<T> {
  listMap: Map<string, T[]>;
  itemKey: T;
  indexWithPlaceholder: number;
  destinationId: string;
  destinationIdByItemKey: Map<T, string>;
  getOriginalList: (parentId: string) => T[];
  getOriginalParentId: (itemKey: T) => string;
}

const moveListItem = <T>({
  listMap,
  itemKey,
  indexWithPlaceholder,
  destinationId,
  destinationIdByItemKey,
  getOriginalList,
  getOriginalParentId,
}: MoveListItemArgs<T>) => {
  const newListMap = new Map(listMap);
  const newDestinationIdByItemKey = new Map(destinationIdByItemKey);

  const originalParentId = getOriginalParentId(itemKey);
  const sourceId = destinationIdByItemKey.get(itemKey) ?? originalParentId;

  // Don't remove the item when:
  // 1. the item is moved within the same parent. `addItemToList` handles
  //    duplications.
  // 2. the source list is its original parent. We want to keep a placeholder
  //    in the original parent.
  if (sourceId !== destinationId && sourceId !== originalParentId) {
    removeItemFromList({
      listMap: newListMap,
      itemKey,
      sourceId,
      destinationIdByItemKey: newDestinationIdByItemKey,
      getOriginalList,
      getOriginalParentId,
    });
  }

  addItemToList({
    listMap: newListMap,
    itemKey,
    indexWithPlaceholder,
    destinationId,
    destinationIdByItemKey: newDestinationIdByItemKey,
    getOriginalList,
    getOriginalParentId,
  });

  return {
    listMap: newListMap,
    destinationIdByItemKey: newDestinationIdByItemKey,
  };
};

const generateReducer = (data: Data) => (state: State, action: Action) => {
  const { treeById } = applyChanges(data, state);
  const { topicPostsMap, topicMap } = data;

  const getOriginalPinnedPostKeys = (topicId: string) => {
    const topicPosts = topicPostsMap?.[topicId];
    return topicPosts?.status === 'loaded'
      ? topicPosts.edges
          .filter((e) => e.pinnedOrder != null)
          .map((e) => getPostKey(e.postId, topicId))
      : [];
  };

  const getTopicOriginalParentId = (topicId: string) => {
    const topic = topicMap.get(topicId);
    if (!topic) return ROOT_TOPIC_ID;
    return topic?.parent?.id ?? ROOT_TOPIC_ID;
  };
  const getTopicOriginalOrderedChildrenIds = (parentTopicId: string) => {
    const topic = topicMap.get(parentTopicId);
    if (!topic) return [];
    return topic.childTopics.map(({ id: topicId }) => topicId);
  };

  switch (action.type) {
    case POST_MOVE: {
      const { destinationIdByPostKey, orderedPinnedPostKeysByTopicId } = state;
      const { postKey, destinationId } = action;
      const originalParentId = getPostOriginalParentId(postKey);

      const newDestinationIdByPostKey = new Map(destinationIdByPostKey);
      const newOrderedPinnedPostKeysByTopicId = new Map(
        orderedPinnedPostKeysByTopicId,
      );

      if (originalParentId === destinationId) {
        newDestinationIdByPostKey.delete(postKey);

        removeItemFromList({
          sourceId: originalParentId,
          listMap: newOrderedPinnedPostKeysByTopicId,
          destinationIdByItemKey: newDestinationIdByPostKey,
          itemKey: postKey,
          getOriginalList: getOriginalPinnedPostKeys,
          getOriginalParentId: getPostOriginalParentId,
        });
      } else {
        newDestinationIdByPostKey.set(postKey, destinationId);
      }

      return {
        ...state,
        destinationIdByPostKey: newDestinationIdByPostKey,
        orderedPinnedPostKeysByTopicId: newOrderedPinnedPostKeysByTopicId,
      };
    }
    case PINNED_POST_MOVE: {
      const {
        destinationIdByPostKey,
        orderedPinnedPostKeysByTopicId,
        pinnedByPostKeys,
      } = state;
      const { postKey, destinationId, indexWithPlaceholder } = action;

      const { listMap, destinationIdByItemKey } = moveListItem({
        listMap: orderedPinnedPostKeysByTopicId,
        itemKey: postKey,
        indexWithPlaceholder,
        destinationId,
        destinationIdByItemKey: destinationIdByPostKey,
        getOriginalList: getOriginalPinnedPostKeys,
        getOriginalParentId: getPostOriginalParentId,
      });

      // Discard move states for posts that are set to pinned/unpinned
      // when they are moved back to the original topic,
      // The reason is pinning/unpinning already triggers index changes,
      // so pin + move should be consider as one action.
      if (
        pinnedByPostKeys.has(postKey) &&
        destinationId === getPostOriginalParentId(postKey)
      ) {
        destinationIdByItemKey.delete(postKey);
      }

      return {
        ...state,
        orderedPinnedPostKeysByTopicId: listMap,
        destinationIdByPostKey: destinationIdByItemKey,
      };
    }
    case TOPIC_MOVE: {
      const { destinationIdByTopicId, orderedChildrenIdsByTopicId } = state;
      const { id, destinationId, indexWithPlaceholder } = action;

      const { listMap, destinationIdByItemKey } = moveListItem({
        listMap: orderedChildrenIdsByTopicId,
        itemKey: id,
        indexWithPlaceholder,
        destinationId: destinationId ?? ROOT_TOPIC_ID,
        destinationIdByItemKey: destinationIdByTopicId,
        getOriginalList: getTopicOriginalOrderedChildrenIds,
        getOriginalParentId: getTopicOriginalParentId,
      });

      return {
        ...state,
        orderedChildrenIdsByTopicId: listMap,
        destinationIdByTopicId: destinationIdByItemKey,
      };
    }
    case TOPIC_SELECTED_SET: {
      const newSelectedTopicIds = new Set(state.selectedTopicIds);
      const newSelectedPostKeys = new Set(state.selectedPostKeys);

      if (action.selected) {
        const tree = treeById.get(action.id);
        tree!.selected.topicIds.forEach((id) => newSelectedTopicIds.delete(id));
        tree!.selected.postKeys.forEach((postKey) =>
          newSelectedPostKeys.delete(postKey),
        );
        tree!.selected.pinnedPostKeys.forEach((postKey) =>
          newSelectedPostKeys.delete(postKey),
        );
        newSelectedTopicIds.add(action.id);
      } else {
        newSelectedTopicIds.delete(action.id);
      }

      return {
        ...state,
        selectedTopicIds: newSelectedTopicIds,
        selectedPostKeys: newSelectedPostKeys,
      };
    }
    case TOPIC_DELETED_SET: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { id, deleted } = action;
      const newStatusByTopicIds = new Map(state.statusByTopicIds);
      if (deleted) {
        newStatusByTopicIds.set(id, TopicStatus.deleted);
      } else {
        newStatusByTopicIds.delete(id);
      }
      return {
        ...state,
        statusByTopicIds: newStatusByTopicIds,
      };
    }
    case TOPIC_NAME_SET: {
      const { id, name } = action;
      const newNameByTopicIds = new Map(state.nameByTopicIds);
      if (name) {
        newNameByTopicIds.set(id, name);
      } else {
        newNameByTopicIds.delete(id);
      }
      return {
        ...state,
        nameByTopicIds: newNameByTopicIds,
      };
    }
    case SUBTOPIC_CREATE: {
      const { topicTempId, parentId } = action;
      const { destinationIdByTopicId, orderedChildrenIdsByTopicId } = state;

      const { listMap, destinationIdByItemKey } = moveListItem({
        listMap: orderedChildrenIdsByTopicId,
        itemKey: topicTempId,
        indexWithPlaceholder: -1,
        destinationId: parentId ?? ROOT_TOPIC_ID,
        destinationIdByItemKey: destinationIdByTopicId,
        getOriginalList: getTopicOriginalOrderedChildrenIds,
        getOriginalParentId: getTopicOriginalParentId,
      });

      const newCreatedTopicIds = new Set(state.createdTopicIds);
      newCreatedTopicIds.add(topicTempId);

      return {
        ...state,
        createdTopicIds: newCreatedTopicIds,
        orderedChildrenIdsByTopicId: listMap,
        destinationIdByTopicId: destinationIdByItemKey,
      };
    }
    case POST_SELECTED_SET: {
      const { postKey } = action;
      const newSelectedPostKeys = new Set(state.selectedPostKeys);

      if (action.selected) {
        newSelectedPostKeys.add(postKey);
      } else {
        newSelectedPostKeys.delete(postKey);
      }

      return {
        ...state,
        selectedPostKeys: newSelectedPostKeys,
      };
    }
    case POST_PINNED_SET: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { postKeys, pinned } = action;
      const {
        pinnedByPostKeys,
        orderedPinnedPostKeysByTopicId,
        destinationIdByPostKey,
      } = state;

      const newDestinationIdByPostKey = new Map(destinationIdByPostKey);
      const newOrderedPinnedPostKeysByTopicId = new Map(
        orderedPinnedPostKeysByTopicId,
      );
      const newPinnedByPostKeys = new Map(pinnedByPostKeys);

      postKeys.forEach((postKey) => {
        const originalParentId = getPostOriginalParentId(postKey);

        const currentDestinationId =
          destinationIdByPostKey.get(postKey) || originalParentId;

        const commonArgs = {
          listMap: newOrderedPinnedPostKeysByTopicId,
          destinationIdByItemKey: newDestinationIdByPostKey,
          itemKey: postKey,
          getOriginalList: getOriginalPinnedPostKeys,
          getOriginalParentId: getPostOriginalParentId,
        };

        if (pinned) {
          addItemToList({
            indexWithPlaceholder: -1,
            destinationId: currentDestinationId,
            ...commonArgs,
          });
        } else {
          removeItemFromList({
            sourceId: currentDestinationId,
            ...commonArgs,
          });
        }

        const originalPinnedPostKeys =
          getOriginalPinnedPostKeys(originalParentId);
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
        const originalPinned = originalPinnedPostKeys.includes(postKey);

        if (pinned === originalPinned) {
          newPinnedByPostKeys.delete(postKey);
        } else {
          newPinnedByPostKeys.set(postKey, pinned);
        }

        if (
          (!pinned || !originalPinned) &&
          currentDestinationId === originalParentId
        ) {
          newDestinationIdByPostKey.delete(postKey);
        }
      });

      return {
        ...state,
        pinnedByPostKeys: newPinnedByPostKeys,
        destinationIdByPostKey: newDestinationIdByPostKey,
        orderedPinnedPostKeysByTopicId: newOrderedPinnedPostKeysByTopicId,
      };
    }
    case POST_ARCHIVED_SET: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { postKeys, archived } = action;
      const newActionsByPostKeys = new Map(state.statusByPostKeys);

      postKeys.forEach((postKey) => {
        if (archived) {
          newActionsByPostKeys.set(postKey, PostStatus.archived);
        } else {
          newActionsByPostKeys.delete(postKey);
        }
      });

      return {
        ...state,
        statusByPostKeys: newActionsByPostKeys,
      };
    }
    case POST_DELETED_SET: {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      const { postKeys, deleted } = action;
      const newActionsByPostKeys = new Map(state.statusByPostKeys);
      postKeys.forEach((postKey) => {
        if (deleted) {
          newActionsByPostKeys.set(postKey, PostStatus.deleted);
        } else if (newActionsByPostKeys.get(postKey) === PostStatus.deleted) {
          newActionsByPostKeys.delete(postKey);
        }
      });
      return {
        ...state,
        statusByPostKeys: newActionsByPostKeys,
      };
    }
    case RESET:
      return generateInitialState();
    default:
      Logger.error('Unhandled action in useTopicReorganization', action);
      return state;
  }
};

const getTopicOperations = (
  parentId: string,
  topicTreeNode: TopicReorganizationTree,
  index: number,
  shouldUpdateExistingOrder: boolean,
): ReorganizeOperation[] => {
  const {
    topic: childTopic,
    reorganizationMetadata: { isNew, move, status, name },
  } = topicTreeNode;

  if (move?.state === MoveStates.movedOut) {
    return [];
  }

  if (status === TopicStatus.deleted) {
    return isNew
      ? // hoist children in deleted new topic
        getOperations({ ...topicTreeNode, topic: { id: parentId } })
      : [
          ...getOperations(topicTreeNode),
          {
            operationName: ReorganizeOperationName.DeleteTopic,
            id: childTopic.id,
          },
        ];
  }

  const operations: ReorganizeOperation[] = [];

  // Order starts at 1
  const order = index + 1;

  const parentIdForOperation = parentId === ROOT_TOPIC_ID ? null : parentId;
  if (isNew) {
    operations.push({
      operationName: ReorganizeOperationName.CreateTopic,
      temporaryId: childTopic.id,
      name: name || childTopic.name,
      parentId: parentIdForOperation,
      order,
    });
  }

  const isMovedIn = move?.state === MoveStates.movedIn;
  const parentUpdates = isMovedIn ? { parentId: parentIdForOperation } : null;
  const orderUpdates =
    isMovedIn ||
    (!isNew &&
      shouldUpdateExistingOrder &&
      childTopic.order !== order &&
      childTopic.id !== UNORGANIZED_TOPIC_ID)
      ? { order }
      : null;
  const nameUpdates = !isNew && name ? { name } : null;

  if (parentUpdates || nameUpdates || orderUpdates) {
    operations.push({
      operationName: ReorganizeOperationName.UpdateTopic,
      id: childTopic.id,
      ...nameUpdates,
      ...parentUpdates,
      ...orderUpdates,
    });
  }

  return [...operations, ...getOperations(topicTreeNode)];
};

function getOperations({
  children,
  topic: { id: parentId },
  reorganizationTopicPosts,
}: Omit<TopicReorganizationTree, 'topic'> & {
  topic: { id: string };
}): ReorganizeOperation[] {
  const shouldUpdateExistingOrder = children.some(
    ({ reorganizationMetadata: { isNew, move } }) =>
      isNew || move?.state === MoveStates.movedIn,
  );

  return [
    ...children.flatMap((topicTreeNode, index) =>
      getTopicOperations(
        parentId,
        topicTreeNode,
        index,
        shouldUpdateExistingOrder,
      ),
    ),
    ...(reorganizationTopicPosts.status === 'loaded'
      ? getPostsOperations({
          parentId,
          reorganizationPostEdges: reorganizationTopicPosts.edges,
        })
      : []),
  ];
}

export const createSpecialTopic = (
  overrides: SetRequired<Partial<TopicForReorganization>, 'name' | 'id'>,
): TopicForReorganization => ({
  childTopics: [],
  ancestors: [],
  parent: null,
  privacy: TopicPrivacy.Open,
  memberEditable: TopicMemberEditable.All,
  inheritedAncestors: [],
  owners: [],
  ownerGroups: [],
  members: [],
  memberGroups: [],
  order: -1,
  ...overrides,
});

const useTopicReorganization = ({
  topic: maybeTopic,
  topics,
  topicPostsMap,
}: {
  topic?: TopicForReorganization;
  topics: TopicForReorganization[];
  topicPostsMap: Record<string, TopicPosts>;
}) => {
  const data = useMemo<Data>(() => {
    const rootTopic =
      maybeTopic ||
      createSpecialTopic({
        id: ROOT_TOPIC_ID,
        name: 'topics',
        childTopics: topics,
      });

    const topicMap = new Map<string, TopicForReorganization>();
    const traverse = (topic: TopicForReorganization) => {
      topicMap.set(topic.id, topic);
      topic.childTopics.forEach(traverse);
    };

    traverse(rootTopic);

    return { rootTopic, topicMap, topicPostsMap };
  }, [maybeTopic, topics, topicPostsMap]);

  const reducer = useMemo(() => generateReducer(data), [data]);
  const [state, dispatch] = useReducer(reducer, generateInitialState());
  const actions = useMemo(() => generateActions(dispatch), [dispatch]);
  const [mutate] = useMutation(REORGANIZE_MUTATION);

  const { changeCount, tree, selectedPosts } = useMemo(
    () => applyChanges(data, state),
    [data, state],
  );
  const operations = useMemo(() => getOperations(tree), [tree]);
  const save = useCallback(
    () => mutate({ variables: { operations } }),
    [mutate, operations],
  );
  const topicReorganization = useMemo(
    () => ({ ...actions, save, operations, changeCount, tree, selectedPosts }),
    [actions, save, operations, changeCount, tree, selectedPosts],
  );

  return topicReorganization;
};

export default useTopicReorganization;
